/**
 * Walkability and pathfinding for the room's 40x40 sub-tile grid.
 *
 * The blocker set is not invented here. `computeFootprintTileMap()`
 * (`src/domain/depthMath.ts`) already answers "which floor sub-tile is
 * physically taken by a piece of furniture" — it is what the drag code uses to
 * refuse an overlapping drop — and it already excludes the things you *can*
 * stand on or under: rugs, windows and wall-mounted items, and anything sitting
 * on a surface. Reusing it means the character can never disagree with the
 * placement rules about what a tile contains.
 *
 * easystarjs runs the search asynchronously so a long path cannot stall a frame.
 */
import EasyStar from 'easystarjs';

import { MAP_WIDTH_TILES } from '../../../domain/grid';
import { computeFootprintTileMap } from '../../../domain/depthMath';
import type { SceneNode } from '../../../domain/sceneNode';

export const WALKABLE = 0;
export const BLOCKED = 1;

/**
 * How far inside the floor's edge the character's anchor must stay, in sub-tiles.
 *
 * Walkability is a test on the anchor, but what the player sees is the sprite:
 * a character standing on a perfectly legal edge tile draws over the floor edge
 * and into the void behind it. So the walkable area shrinks — the cause, not the
 * frame.
 *
 * The number that shrinks it must be the *drawn* half-width, and this constant
 * used to be built from the wrong one. `container.getBounds()` reports ±40
 * room-local px, and `AGENT_RADIUS_TILES` below spends two paragraphs on why
 * that figure is a trap: it is the padded frame, sized for the widest stride of
 * all eight directions. The silhouette the player actually sees, differenced out
 * of live frames, is 26 px. Same projection onto the edge normal,
 * `26 * 12.5 / hypot(12.5, 6.25)` = 23.3 room-local px = 1.9 sub-tiles, rounded
 * up to 2.
 *
 * The old value of 3 was that arithmetic run on the padded frame, and it cost
 * the player a lane: the floor is drawn out to sub-tile 39 while the character
 * was refused everything past 36, so a corridor the player can see between a
 * sofa and the room's near corner did not exist for the pathfinder and she
 * detoured around the whole room (H1). At 2 the anchor may reach sub-tile 37,
 * the feet (1.70 sub-tiles, measured) reach 38.7 and the arms (2.45) reach 39.5
 * — still over floor, which is the only thing this margin is defending.
 */
export const EDGE_MARGIN_TILES = 2;

/**
 * Body radius, in sub-tiles — the character is an AGENT, not a piece of furniture.
 *
 * Furniture is a static obstacle: it owns tiles, they are cut out of the walkable
 * set, and it moves rarely. A character is a dynamic agent with a *radius*: it
 * stays a point in the search, and the obstacles are grown by that radius instead
 * (Minkowski sum / clearance-based A*). That is the standard split — giving the
 * character a footprint in the furniture structure is the thing that makes agents
 * "wear" the geometry and squeeze through gaps a body could not.
 *
 * The number is measured from the atlas, not guessed. Compositing all four
 * layers of all 192 frames and taking the silhouette of the bottom 12 % of the
 * figure — the feet, which is what actually rests on the floor — gives a
 * half-width of 27 px median and 33 px worst case, on a figure 187 px tall that
 * stands 1.75 m in world units (≈107 room-local px per metre, so the feet span
 * ~0.55 m: a human, not a sprite-sized guess).
 *
 * One sub-tile spans 25 px on screen-x and 12.5 on screen-y, so a ground-space
 * radius of r sub-tiles shows up as at most `r * 25 / √2 ≈ 17.7r` px of
 * half-width. 33 px therefore needs r ≈ 1.87.
 *
 * r = 1 was that same calculation rounded *down*, and it is exactly the reported
 * bug: the anchor tile is legally free while the body it carries is drawn a
 * sub-tile deep into the sofa. r = 2 is the honest ceiling.
 *
 * The upper bound is navigability, and it is close: measured against the live
 * furniture of room 38, r = 2 leaves 340 walkable tiles in a *single* connected
 * component, while r = 2.5 leaves 296 tiles broken into islands (largest 162) —
 * a room where the sofa corner walls the character out of its own bedroom. So
 * 2 is not a round number chosen for looks, it is the largest radius the room
 * survives.
 *
 * (The walk stride reaches much further — up to 69 px half-width mid-step — but
 * that is the swing leg in the air. Ground occupancy is the supporting foot.)
 *
 * Confirmed a second time from the *running game*, by a method that shares
 * nothing with the atlas arithmetic above: hide the actor, capture the frame,
 * show it, capture again, and the differing pixels are the silhouette the player
 * sees (`lib/probes/silhouette.mjs`). Over 64 frames spanning all eight
 * directions the feet band measures r = 1.70 sub-tiles worst case, 1.51 at p95;
 * the whole body including arms reaches 2.45.
 *
 * Beware the trap that measurement exists to close: `container.getBounds()`
 * reports 43.2 px of width, which converts to r = 4.07 — and that is the padded
 * *frame*, sized for the widest stride of all eight directions, not the body.
 * The drawn silhouette is 26 px. Do not raise this constant on frame bounds; the
 * room does not stay connected at r = 2.5.
 */
export const AGENT_RADIUS_TILES = 2;

/**
 * Clearance required of a tile the character *stops* on, in sub-tiles.
 *
 * `AGENT_RADIUS_TILES` is the radius that keeps the room crossable, and it is
 * genuinely the largest one that does: rebuilding this grid from the live
 * footprints of a furnished room gives 341 walkable tiles in a single connected
 * component at r = 2, but 296 tiles in *two* components (162 + 134) at r = 2.3,
 * and five components at r = 3. Raising it globally does not tighten the walk,
 * it cuts the room in half.
 *
 * But a radius that only just keeps the room connected also lets the character
 * come to rest with no daylight left. Worked example, measured off a live frame:
 * anchor (25, 6), nearest blocked tile (23, 5) — distance sqrt(5) = 2.236, so
 * the anchor is legal. Yet the planted foot reaches 1.70 sub-tiles toward the
 * table (silhouette measurement, `lib/probes/silhouette.mjs`) and furniture art
 * covers its outermost footprint tile to that tile's far edge, half a sub-tile
 * past the centre the grid stores. Required: 1.70 + 0.5 = 2.20. Available:
 * 2.236. The 0.036 sub-tile of margin is 0.4 screen px, and the shoe lands
 * exactly on the table leg.
 *
 * So the two cases are separated. Squeezing past a table *while walking* is a
 * moment of motion and reads as passing; standing in one is what the eye
 * catches. Traversal keeps r = 2; a resting tile must clear this larger radius,
 * and when none is available near the requested tile the code falls back to the
 * plain walkable tile rather than refusing to move.
 */
export const REST_CLEARANCE_TILES = 2.5;

export interface TileXY {
  tileX: number;
  tileY: number;
}

/**
 * Build the walkability grid. `grid[tileY][tileX]`, which is the row-major order
 * easystarjs expects — note the axis order is the opposite of the game's
 * `(tileX, tileY)` convention and mixing them up silently mirrors every path.
 */
export function buildWalkGrid(
  markers: Map<number, SceneNode>,
  radius = AGENT_RADIUS_TILES,
): number[][] {
  const blocked = computeFootprintTileMap(markers);
  const m = EDGE_MARGIN_TILES;
  const r = radius;

  // Offsets of the body disc: every tile within `radius` of the anchor. The
  // loop bound is ceil'd because the radius is not required to be an integer —
  // REST_CLEARANCE_TILES is 2.5, and stepping the loop by the radius itself
  // would walk half-tile offsets that index nothing.
  const rInt = Math.ceil(r);
  const disc: Array<[number, number]> = [];
  for (let dy = -rInt; dy <= rInt; dy++) {
    for (let dx = -rInt; dx <= rInt; dx++) {
      if (dx * dx + dy * dy <= r * r) disc.push([dx, dy]);
    }
  }

  const grid: number[][] = [];
  for (let ty = 0; ty < MAP_WIDTH_TILES; ty++) {
    const row = new Array<number>(MAP_WIDTH_TILES).fill(WALKABLE);
    for (let tx = 0; tx < MAP_WIDTH_TILES; tx++) {
      const nearEdge =
        tx < m || ty < m || tx >= MAP_WIDTH_TILES - m || ty >= MAP_WIDTH_TILES - m;
      // Grow the obstacles by the body radius rather than growing the agent:
      // the anchor is walkable only if the whole disc around it is clear.
      const bodyHitsFurniture = disc.some(([dx, dy]) =>
        blocked.has(`${tx + dx},${ty + dy}`),
      );
      if (nearEdge || bodyHitsFurniture) row[tx] = BLOCKED;
    }
    grid.push(row);
  }
  return grid;
}

export function isInsideRoom({ tileX, tileY }: TileXY): boolean {
  return (
    tileX >= 0 && tileY >= 0 && tileX < MAP_WIDTH_TILES && tileY < MAP_WIDTH_TILES
  );
}

export function isWalkable(grid: number[][], { tileX, tileY }: TileXY): boolean {
  return isInsideRoom({ tileX, tileY }) && grid[tileY][tileX] === WALKABLE;
}

/**
 * Nearest walkable tile to `target`, searched in rings.
 *
 * Clicking a sofa should walk you *to* the sofa, not do nothing — which is what
 * The Sims does, and it is also what keeps the controls from feeling broken when
 * a tap lands a pixel inside a footprint.
 */
export function nearestWalkable(
  grid: number[][],
  target: TileXY,
  maxRadius = 8,
): TileXY | null {
  if (isWalkable(grid, target)) return target;
  for (let r = 1; r <= maxRadius; r++) {
    let best: TileXY | null = null;
    let bestDist = Infinity;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const cand = { tileX: target.tileX + dx, tileY: target.tileY + dy };
        if (!isWalkable(grid, cand)) continue;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          best = cand;
        }
      }
    }
    if (best) return best;
  }
  return null;
}

/**
 * Where the character should come to rest for a click on `target`.
 *
 * Two grids, one answer: `restGrid` is the same room inflated by
 * `REST_CLEARANCE_TILES`, so a tile that is walkable in it has daylight around
 * the standing sprite. Prefer such a tile; fall back to the merely-walkable one
 * when the roomier grid has nothing nearby, because refusing to move is a worse
 * bug than standing close to a table. `searchRadius` is deliberately small — a
 * click means "go *there*", and silently walking six tiles away to find elbow
 * room would be its own defect.
 */
export function restingTileFor(
  grid: number[][],
  restGrid: number[][],
  target: TileXY,
  searchRadius = 3,
): TileXY | null {
  const roomy = nearestWalkable(restGrid, target, searchRadius);
  // The roomier grid is a subset of the walkable one, but only when both were
  // built from the same markers; verify rather than assume, since a stale
  // restGrid would otherwise send the actor into furniture.
  if (roomy && isWalkable(grid, roomy)) return roomy;
  return nearestWalkable(grid, target);
}

export interface PathRequest {
  cancel: () => void;
}

/**
 * Async A* from `from` to `to`. Resolves with `null` when no path exists.
 *
 * Diagonals are allowed but corner-cutting is not: without
 * `disableCornerCutting`, a path will slip through the diagonal gap between two
 * furniture corners, which looks exactly like walking through furniture.
 */
export function findPath(
  grid: number[][],
  from: TileXY,
  to: TileXY,
  onDone: (path: TileXY[] | null) => void,
): PathRequest {
  const easystar = new EasyStar.js();
  easystar.setGrid(grid);
  easystar.setAcceptableTiles([WALKABLE]);
  easystar.enableDiagonals();
  easystar.disableCornerCutting();
  easystar.setIterationsPerCalculation(1200);

  let cancelled = false;
  const instanceId = easystar.findPath(
    from.tileX,
    from.tileY,
    to.tileX,
    to.tileY,
    (path) => {
      if (cancelled) return;
      onDone(path ? path.map((p) => ({ tileX: p.x, tileY: p.y })) : null);
    },
  );

  // easystarjs only advances its search when calculate() is pumped.
  let raf = 0;
  const pump = () => {
    if (cancelled) return;
    easystar.calculate();
    raf = requestAnimationFrame(pump);
  };
  raf = requestAnimationFrame(pump);

  return {
    cancel: () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      try {
        easystar.cancelPath(instanceId);
      } catch {
        /* already finished */
      }
    },
  };
}

/**
 * Drop waypoints that lie on the straight line between their neighbours.
 *
 * A* returns one waypoint per tile, so a straight corridor arrives as twenty
 * collinear points. Collapsing them means the actor turns only where the path
 * actually turns, instead of re-deciding its facing every tile.
 */
export function simplifyPath(path: TileXY[]): TileXY[] {
  if (path.length < 3) return path;
  const out: TileXY[] = [path[0]];
  for (let i = 1; i < path.length - 1; i++) {
    const a = out[out.length - 1];
    const b = path[i];
    const c = path[i + 1];
    const abx = Math.sign(b.tileX - a.tileX);
    const aby = Math.sign(b.tileY - a.tileY);
    const bcx = Math.sign(c.tileX - b.tileX);
    const bcy = Math.sign(c.tileY - b.tileY);
    if (abx !== bcx || aby !== bcy) out.push(b);
  }
  out.push(path[path.length - 1]);
  return out;
}
